import { useCallback, useMemo, useRef } from 'react'

import {
  useInitializeTabletWorkoutFavoritesMutation,
  useTabletWorkoutFavoritesQuery,
  useToggleTabletWorkoutFavoriteMutation,
} from 'api/queries/tablet-workout-favorites'
import { INRWorkoutSession, INRWorkoutTemplate } from 'interfaces/workout'
import {
  MAX_FAVORITE_WORKOUTS,
  ToggleFavoriteResult,
  createFavoriteWorkoutInput,
  favoriteWorkoutFromApi,
  favoriteWorkoutsFromApi,
  isWorkoutFavorite,
} from 'lib/favoriteWorkouts'

const EMPTY_TOGGLE_RESULT: ToggleFavoriteResult = {
  favorite: null,
  added: false,
  limitReached: false,
}

const useFavoriteWorkouts = () => {
  const { data, loading: queryLoading } = useTabletWorkoutFavoritesQuery()
  const [runToggle, { loading: toggleLoading }] = useToggleTabletWorkoutFavoriteMutation()
  const [runInitialize, { loading: initializeLoading }] = useInitializeTabletWorkoutFavoritesMutation()
  const initializationAttempted = useRef(false)
  const favorites = useMemo(
    () => favoriteWorkoutsFromApi(data?.tabletWorkoutFavorites ?? []),
    [data?.tabletWorkoutFavorites],
  )

  const toggleFavorite = useCallback(async (session: INRWorkoutSession): Promise<ToggleFavoriteResult> => {
    const isRemoving = isWorkoutFavorite(session, favorites)
    if (!isRemoving && favorites.length >= MAX_FAVORITE_WORKOUTS) {
      return { ...EMPTY_TOGGLE_RESULT, limitReached: true }
    }

    const input = createFavoriteWorkoutInput(session, favorites)
    if (!input) return EMPTY_TOGGLE_RESULT

    try {
      const response = await runToggle({ variables: { input } })
      const result = response.data?.toggleTabletWorkoutFavorite
      if (!result) return EMPTY_TOGGLE_RESULT

      return {
        added: result.added,
        favorite: result.favorite ? favoriteWorkoutFromApi(result.favorite) : null,
        limitReached: result.limitReached,
      }
    } catch (error) {
      console.error('Could not update tablet workout favorite', error)
      return EMPTY_TOGGLE_RESULT
    }
  }, [favorites, runToggle])

  const initializeDefault = useCallback(async (workoutTemplate?: INRWorkoutTemplate): Promise<boolean> => {
    if (!workoutTemplate || initializationAttempted.current) return false
    initializationAttempted.current = true
    try {
      await runInitialize({ variables: { templateId: workoutTemplate.id } })
      return true
    } catch (error) {
      console.error('Could not initialize tablet workout favorites', error)
      return false
    }
  }, [runInitialize])

  const isFavorite = useCallback(
    (session: INRWorkoutSession) => isWorkoutFavorite(session, favorites),
    [favorites],
  )

  return {
    favorites,
    initializeDefault,
    isFavorite,
    toggleFavorite,
    loading: queryLoading || toggleLoading || initializeLoading,
  }
}

export default useFavoriteWorkouts
